import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import ResellerSidebar from "@/pages/reseller/components/ResellerSidebar";
import { mockFactoryProfiles } from "@/mocks/factoryProfile";
import { mockResellerUser } from "@/mocks/reseller";

const platformIcons: Record<string, string> = {
  TikTok: "ri-tiktok-line",
  YouTube: "ri-youtube-line",
  Instagram: "ri-instagram-line",
  Douyin: "ri-tiktok-line",
  WeChat: "ri-wechat-line",
  Twitter: "ri-twitter-x-line",
};

const warehouseFlag: Record<string, string> = {
  USA: "🇺🇸",
  Canada: "🇨🇦",
  Both: "🌎",
};

const FactoryProfilePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<{ name: string; url: string; commission: number; price: number } | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [promotingIds, setPromotingIds] = useState<Set<string>>(new Set());

  const factory = mockFactoryProfiles.find((f) => f.slug === slug) || mockFactoryProfiles[0];

  const generateLink = (product: typeof factory.products[0]) => {
    const url = "https://crownprimemall.com/p/" + product.slug + "?ref=" + mockResellerUser.referralCode;
    setGeneratedLink({ name: product.name, url, commission: product.commission, price: product.price });
    setPromotingIds((prev) => new Set(prev).add(product.id));
  };

  const copyLink = (url: string, id: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedSlug(id);
      setTimeout(() => setCopiedSlug(null), 2000);
    });
  };

  const maxCommission = Math.max(...Object.values(factory.commissionRates));

  return (
    <div className="min-h-screen bg-[#f8f7f5] flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ResellerSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center gap-4 sticky top-0 z-20">
          <button className="lg:hidden w-8 h-8 flex items-center justify-center text-gray-600 cursor-pointer" onClick={() => setSidebarOpen(true)}>
            <i className="ri-menu-line text-xl"></i>
          </button>
          <Link to="/reseller/products" className="flex items-center gap-2 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer text-sm">
            <i className="ri-arrow-left-line"></i>
            Back to Browse
          </Link>
          <div className="h-4 w-px bg-gray-200"></div>
          <div className="text-sm font-semibold text-gray-900 truncate">{factory.name}</div>
        </header>

        <main className="flex-1">
          {/* Cover Image */}
          <div className="relative w-full h-56 overflow-hidden bg-gray-200">
            <img src={factory.coverImage} alt={factory.name} className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            {factory.verified && (
              <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                <i className="ri-shield-check-fill"></i>
                Verified Factory
              </div>
            )}
          </div>

          <div className="px-6 pb-8">
            {/* Factory Header Card */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 -mt-10 relative z-10 mb-6">
              <div className="flex flex-col md:flex-row gap-5 items-start">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gray-100 flex-shrink-0 border-4 border-white">
                  <img src={factory.logo} alt={factory.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h1 className="text-xl font-bold text-gray-900">{factory.name}</h1>
                    <span className="text-lg">{factory.flag}</span>
                    {factory.verified && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        <i className="ri-shield-check-fill"></i> Verified
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{factory.location}</span>
                    <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>Est. {factory.founded}</span>
                    <span className="flex items-center gap-1"><i className="ri-price-tag-3-line"></i>{factory.category}</span>
                    <span className="flex items-center gap-1"><i className="ri-time-line"></i>Responds {factory.responseTime}</span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{factory.description}</p>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0 text-center">
                  <div className="bg-[#C41E3A]/10 rounded-xl px-5 py-3">
                    <div className="text-2xl font-extrabold text-[#C41E3A]">Up to {maxCommission}%</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">Max Commission Rate</div>
                  </div>
                  <div className="text-xs text-gray-400 flex items-center justify-center gap-1">
                    <i className="ri-paypal-line text-[#003087]"></i>
                    Paid via PayPal monthly
                  </div>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100">
                {[
                  { icon: "ri-star-fill", color: "text-amber-400", value: factory.rating, label: `${factory.reviewCount.toLocaleString()} reviews` },
                  { icon: "ri-shopping-cart-line", color: "text-[#C41E3A]", value: factory.totalSales.toLocaleString(), label: "Total Sales" },
                  { icon: "ri-user-star-line", color: "text-[#D4AF37]", value: factory.activeResellers, label: "Active Resellers" },
                  { icon: "ri-award-line", color: "text-green-600", value: factory.certifications.length, label: "Certifications" },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <i className={stat.icon + " " + stat.color + " text-sm"}></i>
                      <span className="text-base font-bold text-gray-900">{stat.value}</span>
                    </div>
                    <div className="text-[10px] text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Products */}
              <div className="lg:col-span-2 space-y-4">
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-box-3-line text-[#C41E3A]"></i>
                  Products from This Factory
                </h2>
                {factory.products.map((product) => {
                  const isPromoting = promotingIds.has(product.id);
                  const earn = ((product.price * product.commission) / 100).toFixed(2);
                  return (
                    <div key={product.id} className="bg-white rounded-xl border border-gray-100 p-4 flex gap-4 hover:border-[#D4AF37]/40 transition-all">
                      <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-start gap-2 mb-1">
                          <div className="text-sm font-semibold text-gray-900 leading-tight flex-1">{product.name}</div>
                          {product.badge && (
                            <span className="text-[10px] font-bold bg-[#C41E3A] text-white px-2 py-0.5 rounded-full whitespace-nowrap">{product.badge}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mb-2">
                          <span>{warehouseFlag[product.warehouse]} {product.warehouse}</span>
                          <span className="flex items-center gap-1"><i className="ri-star-fill text-amber-400"></i>{product.rating}</span>
                          <span>{product.totalSales.toLocaleString()} sold</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div>
                              <span className="text-base font-bold text-gray-900">${product.price.toFixed(2)}</span>
                              <div className="text-[10px] text-gray-400">per unit</div>
                            </div>
                            <div className="bg-[#C41E3A]/10 rounded-lg px-3 py-1.5 text-center">
                              <div className="text-sm font-bold text-[#C41E3A]">{product.commission}% → ${earn}</div>
                              <div className="text-[10px] text-gray-400">you earn / sale</div>
                            </div>
                          </div>
                          <button
                            onClick={() => generateLink(product)}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                              isPromoting
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-[#C41E3A] text-white hover:bg-[#a01830]"
                            }`}
                          >
                            <i className={isPromoting ? "ri-checkbox-circle-line" : "ri-links-line"}></i>
                            {isPromoting ? "Promoting" : "Get Link"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right: Commission Rates + Info */}
              <div className="space-y-5">
                {/* Commission by Platform */}
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-percent-line text-[#D4AF37]"></i>
                    Commission by Platform
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(factory.commissionRates).map(([platform, rate]) => (
                      <div key={platform} className="flex items-center gap-3">
                        <div className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0">
                          <i className={(platformIcons[platform] || "ri-share-line") + " text-sm text-gray-600"}></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-gray-700">{platform}</span>
                            <span className="text-xs font-bold text-[#C41E3A]">{rate}%</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#C41E3A] to-[#D4AF37] rounded-full transition-all"
                              style={{ width: (rate / 40) * 100 + "%" }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400 flex items-center gap-1.5">
                    <i className="ri-information-line text-[#D4AF37]"></i>
                    Individual products may have custom rates
                  </div>
                </div>

                {/* Certifications */}
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-award-line text-green-600"></i>
                    Certifications
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {factory.certifications.map((cert) => (
                      <span key={cert} className="flex items-center gap-1 text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded-full">
                        <i className="ri-shield-check-line text-xs"></i>
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Payout Info */}
                <div className="bg-[#fdf8f0] border border-[#D4AF37]/30 rounded-xl p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-paypal-line text-[#003087]"></i>
                    Payout Details
                  </h3>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Min. Payout</span>
                      <span className="font-semibold">${factory.minPayout}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Schedule</span>
                      <span className="font-semibold">{factory.payoutSchedule}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Method</span>
                      <span className="font-semibold flex items-center gap-1"><i className="ri-paypal-line text-[#003087]"></i> PayPal</span>
                    </div>
                  </div>
                </div>

                {/* Supported Platforms */}
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-share-line text-gray-500"></i>
                    Supported Platforms
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {factory.platforms.map((p) => (
                      <div key={p} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-2.5 py-1.5 rounded-lg">
                        <i className={(platformIcons[p] || "ri-share-line") + " text-xs text-gray-600"}></i>
                        <span className="text-xs text-gray-600 font-medium">{p}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Link Generated Modal */}
      {generatedLink && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-xl">
                  <i className="ri-links-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Referral Link Ready!</h2>
                  <p className="text-xs text-gray-400">Share to earn commission</p>
                </div>
              </div>
              <button onClick={() => setGeneratedLink(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm font-semibold text-gray-900 line-clamp-2">{generatedLink.name}</div>
              <div className="grid grid-cols-3 gap-3 bg-[#fdf8f0] border border-[#D4AF37]/30 rounded-xl p-4 text-center">
                <div>
                  <div className="text-base font-bold text-gray-900">${generatedLink.price.toFixed(2)}</div>
                  <div className="text-[10px] text-gray-400">Price</div>
                </div>
                <div className="border-x border-[#D4AF37]/20">
                  <div className="text-base font-bold text-[#D4AF37]">{generatedLink.commission}%</div>
                  <div className="text-[10px] text-gray-400">Rate</div>
                </div>
                <div>
                  <div className="text-base font-bold text-[#C41E3A]">${((generatedLink.price * generatedLink.commission) / 100).toFixed(2)}</div>
                  <div className="text-[10px] text-gray-400">You Earn</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl p-3">
                <div className="flex-1 text-xs text-gray-600 truncate font-mono">{generatedLink.url}</div>
                <button
                  onClick={() => copyLink(generatedLink.url, "modal")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                    copiedSlug === "modal" ? "bg-green-600 text-white" : "bg-[#C41E3A] text-white hover:bg-[#a01830]"
                  }`}
                >
                  <i className={copiedSlug === "modal" ? "ri-checkbox-circle-line" : "ri-file-copy-line"}></i>
                  {copiedSlug === "modal" ? "Copied!" : "Copy"}
                </button>
              </div>
              <button onClick={() => setGeneratedLink(null)} className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FactoryProfilePage;
